import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article
    ? getRelatedArticles(article.slug, article.category)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-600">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-200 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.image,
        articleSection: article.categoryName,
        publisher: {
          '@type': 'Organization',
          name: '欲望之岛',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mindvoyage.github.io' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://mindvoyage.github.io/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <StructuredData data={articleSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <Breadcrumb
          items={[
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-8 md:py-12 bg-background-50">
          <div className="container-custom max-w-3xl">
            <header className="mb-8 md:mb-10">
              <Link
                to={`/category/${article.category}`}
                className="inline-block text-xs px-3 py-1 rounded-full bg-accent-500/10 text-accent-400 mb-4 font-label hover:bg-accent-500/20 transition-colors"
              >
                {article.categoryName}
              </Link>

              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-100 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="relative h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-10">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${article.image})` }}
                role="img"
                aria-label={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/50">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-500/10 text-secondary-400 font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/50">
                <h3 className="text-lg md:text-xl font-semibold font-heading text-foreground-200 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="card-dark group cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-300 font-heading mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{rel.summary}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}